/**
 * The share and copy buttons under a share-friendly, on a 词典 entry or a 例句.
 *
 * `src/page-render/share-friendly-markup.ts` draws both `hidden`, beside the
 * two downloads, and this shows each one where the browser can do what it
 * says. A browser that cannot is left with the downloads, which are plain
 * links and need nothing from here.
 *
 * **Share sends the GIF as a file, through the system share sheet.** That is
 * the one route found by hand that lands a GIF in a chat as a GIF.
 * `docs/share-friendlies.md` under "Sharing" has the Telegram test. The file
 * is fetched before the button is shown rather than when it is pressed,
 * because Safari lets a page share only inside the gesture that asked, and a
 * fetch on the click can outlast it. The fetch waits for the picture, which is
 * the same GIF and loads lazily, so it costs nothing the picture had not
 * already cost and reads from the cache the picture filled.
 *
 * **The buttons are found by their data attributes,** which the cards at the
 * foot of an entry, the blocks on `/chinese-meme-reaction-gifs/` and a drawn
 * birthday GIF all carry. Each dresses its buttons differently, as links, as
 * Web Awesome's native buttons and as `wa-button`, so the classes are left to
 * the stylesheets, and Share is any element marked `data-share-gif`.
 *
 * **On the reaction GIFs' page the GIF waits for a press.** Its picture is a
 * light preview rather than the GIF (`layouts/partials/biaoqing-writing.html`),
 * and fetching every GIF a reader scrolled past would cost the megabytes the
 * preview saves. A button marked `data-share-on-press` is shown once the
 * browser says it can share a GIF, starts the fetch when a pointer or focus
 * reaches it, and shares on the click. A fetch that outlasts the gesture is
 * refused by Safari, and the button then asks for another tap, which shares the
 * file already in hand.
 *
 * **Nothing else goes with the file.** A title or the page's address beside
 * it makes some targets send the text and drop the picture.
 *
 * **Copy link copies the GIF's own address**, which a chat or a forum unfurls
 * into the moving card. Browsers put a PNG on the clipboard and refuse a GIF,
 * so the address is the closest a button can come to copying the picture.
 */

import { offerLink } from "./share-friendly-link.js";

/** What the share sheet and the clipboard need, as a page is given them. */
export interface ShareFriendlyBrowser {
  readonly fetch: (url: string) => Promise<Response>;
  readonly navigator: Pick<Navigator, "share" | "canShare" | "clipboard">;
}

/**
 * Show and wire every share-friendly's buttons under `root`.
 * @param root - The page, or the part of it to look in.
 * @param browser - The fetch and navigator to use. The window's own unless given.
 */
export function startShareFriendlies(
  root: ParentNode,
  browser: ShareFriendlyBrowser = {
    fetch: async (url) => globalThis.fetch(url),
    navigator,
  },
): void {
  for (const section of root.querySelectorAll(".share-friendly")) {
    offerSharing(section, browser);
    offerLink(section, browser);
  }
}

/**
 * Show the share button once the GIF is in hand and the browser says it can
 * share it. Left hidden on a browser with no file sharing, which is most of
 * the desktop ones.
 */
function offerSharing(section: Element, browser: ShareFriendlyBrowser): void {
  const button = section.querySelector<HTMLElement>("[data-share-gif]");
  const image = section.querySelector<HTMLImageElement>("img");
  const { share, canShare } = browser.navigator;
  const gif = button?.dataset["shareGif"];
  const name = button?.dataset["shareName"];

  if (button?.dataset["shareOnPress"] !== undefined) {
    offerSharingOnPress(button, browser);
    return;
  }

  if (
    button === null ||
    image === null ||
    typeof share !== "function" ||
    typeof canShare !== "function" ||
    gif === undefined ||
    name === undefined
  ) {
    return;
  }

  const ready = async (): Promise<void> => {
    const response = await browser.fetch(gif);

    if (!response.ok) {
      return;
    }

    const file = new File([await response.blob()], name, {
      type: "image/gif",
    });
    const files = [file];

    if (!browser.navigator.canShare({ files })) {
      return;
    }

    button.addEventListener("click", () => {
      // A reader closing the sheet without choosing is an AbortError, and
      // there is nothing to tell them about it.
      browser.navigator.share({ files }).catch(() => undefined);
    });
    shown(button);
  };
  const whenLoaded = (): void => {
    ready().catch(() => undefined);
  };

  if (image.complete && image.naturalWidth > 0) {
    whenLoaded();
  } else {
    image.addEventListener("load", whenLoaded, { once: true });
  }
}

/**
 * Show a share button that fetches its GIF when a reader reaches for it,
 * rather than when the picture arrives.
 */
function offerSharingOnPress(
  button: HTMLElement,
  browser: ShareFriendlyBrowser,
): void {
  const { share, canShare } = browser.navigator;
  const gif = button.dataset["shareGif"];
  const name = button.dataset["shareName"];

  if (
    typeof share !== "function" ||
    typeof canShare !== "function" ||
    gif === undefined ||
    name === undefined ||
    // Called on the navigator, since Safari throws on an unbound `canShare`,
    // and the throw stopped every button and loop on the page after it.
    !browser.navigator.canShare({
      files: [new File([], name, { type: "image/gif" })],
    })
  ) {
    return;
  }

  let file: Promise<File | undefined> | undefined;
  const fetched = async (): Promise<File | undefined> => {
    file ??= browser
      .fetch(gif)
      .then(async (response) =>
        response.ok
          ? new File([await response.blob()], name, { type: "image/gif" })
          : undefined,
      )
      .catch(() => undefined);

    return file;
  };
  const reach = (): void => {
    fetched().catch(() => undefined);
  };

  button.addEventListener("pointerenter", reach);
  button.addEventListener("pointerdown", reach);
  button.addEventListener("focus", reach);
  const shareFetched = async (): Promise<void> => {
    const files = await fetched();

    if (files === undefined) {
      return;
    }

    await browser.navigator
      .share({ files: [files] })
      .catch((error: unknown) => {
        // The sheet closed without a choice is an AbortError. A refusal is the
        // gesture having run out while the GIF arrived, and the next tap has it.
        if (error instanceof Error && error.name === "NotAllowedError") {
          const said = button.querySelector("[data-share-said]");

          if (said !== null) {
            said.textContent = "Tap again to share";
          }
        }
      });
  };

  button.addEventListener("click", () => {
    shareFetched().catch(() => undefined);
  });
  shown(button);
}

/** Show a button, which is hidden by way of the list item holding it. */
export function shown(button: HTMLElement): void {
  const item = button.closest("li");

  if (item !== null) {
    item.hidden = false;
  }
}
